import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type JSX,
} from "react";
import { useSearchParams } from "react-router-dom";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import { hasAdminPermission } from "../../contracts/admin-permissions";
import type {
  AdminConversationAccessPurpose,
  AdminConversationQuery,
  AdminConversationSource,
} from "../../contracts/admin-read-models";
import { pickAdminDashboardLocaleText } from "../../shared/locale/admin-dashboard-copy";
import { useAdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale-context";
import { withAdminDashboardLocaleSearchParams } from "../../shared/locale/admin-dashboard-locale-routing";
import { useAdminManualRefreshRegistration } from "../../shared/query/admin-manual-refresh-context";
import { AdminDataTable } from "../../shared/ui/admin-data-table";
import { AdminEmptyState } from "../../shared/ui/admin-empty-state";
import { AdminErrorState } from "../../shared/ui/admin-error-state";
import { AdminFilterBar } from "../../shared/ui/admin-filter-bar";
import type {
  ConversationHistoryViewModel,
  ConversationTranscriptViewModel,
} from "./conversation-history-view-model";
import {
  useConversationContentAccessModel,
  useConversationHistoryScreenModel,
} from "./use-conversation-history-screen-model";

export type ConversationHistoryScreenProps = {
  dependencies: AdminDashboardDependencies;
};

type ConversationRow = ConversationHistoryViewModel["rows"][number];

const cardStyle = {
  padding: "20px",
  borderRadius: "22px",
  background: "rgba(255, 255, 255, 0.82)",
  border: "1px solid rgba(24, 33, 43, 0.08)",
  boxShadow: "0 18px 36px rgba(15, 23, 42, 0.08)",
} as const;

const controlStyle = {
  width: "100%",
  minHeight: "40px",
  padding: "10px 12px",
  borderRadius: "14px",
  border: "1px solid rgba(148, 163, 184, 0.45)",
  background: "#fff",
  color: "#18212b",
  fontSize: "0.92rem",
  boxSizing: "border-box",
} as const;

function parseConversationQuery(params: URLSearchParams): AdminConversationQuery {
  const source = params.get("source");
  return {
    from: params.get("from")?.trim() || undefined,
    to: params.get("to")?.trim() || undefined,
    source: source === "owner" || source === "public_pet" ? source : undefined,
    internalUserId: params.get("internalUserId")?.trim() || undefined,
    limit: 100,
  };
}

function createFilterSearchParams(formData: FormData, locale: "ko" | "en"): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of ["from", "to", "source", "internalUserId"]) {
    const value = formData.get(key);
    if (typeof value === "string" && value.trim().length > 0) {
      params.set(key, value.trim());
    }
  }
  return withAdminDashboardLocaleSearchParams(params, locale);
}

function primaryButtonStyle(disabled = false) {
  return {
    minHeight: "40px",
    padding: "0 16px",
    borderRadius: "999px",
    border: "none",
    background: disabled ? "#94a3b8" : "#0f766e",
    color: "#fff",
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
  } as const;
}

export function ConversationTranscript(props: {
  auditId: string;
  auditLabel: string;
  messages: ConversationTranscriptViewModel["messages"];
  transcriptLabel: string;
}): JSX.Element {
  return (
    <section
      aria-label={props.transcriptLabel}
      data-conversation-transcript="true"
      style={{
        display: "grid",
        gap: "16px",
        padding: "16px",
        borderRadius: "20px",
        background: "#f4f1eb",
        border: "1px solid rgba(120, 113, 108, 0.16)",
      }}
    >
      <p style={{ margin: 0, color: "#64748b", fontSize: "0.82rem", overflowWrap: "anywhere" }}>
        {props.auditLabel}: {props.auditId}
      </p>
      <div style={{ display: "grid", gap: "14px" }}>
        {props.messages.map((message) => {
          const outgoing = message.role === "user";
          return (
            <article
              aria-label={`${message.roleLabel} · ${message.createdAtLabel}`}
              data-message-alignment={outgoing ? "outgoing" : "incoming"}
              data-message-role={message.role}
              key={message.messageId}
              style={{ display: "flex", justifyContent: outgoing ? "flex-end" : "flex-start", minWidth: 0 }}
            >
              <div style={{ display: "grid", gap: "5px", maxWidth: "82%", minWidth: 0 }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: outgoing ? "flex-end" : "flex-start",
                    gap: "8px",
                    color: "#64748b",
                    fontSize: "0.76rem",
                  }}
                >
                  <strong>{message.roleLabel}</strong>
                  <time dateTime={message.createdAt}>{message.createdAtLabel}</time>
                </div>
                <div
                  style={{
                    padding: "12px 15px",
                    borderRadius: outgoing ? "20px 20px 6px 20px" : "20px 20px 20px 6px",
                    background: outgoing ? "#0f766e" : "#ffffff",
                    color: outgoing ? "#ffffff" : "#18212b",
                    border: outgoing ? "1px solid #0f766e" : "1px solid rgba(148, 163, 184, 0.28)",
                    boxShadow: "0 7px 18px rgba(15, 23, 42, 0.06)",
                  }}
                >
                  <p style={{ margin: 0, whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.65 }}>{message.text}</p>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

export function ConversationHistoryScreen(props: ConversationHistoryScreenProps): JSX.Element {
  const { locale } = useAdminDashboardLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [manualRefreshSignal, setManualRefreshSignal] = useState(0);
  const [selectedRow, setSelectedRow] = useState<ConversationRow | undefined>();
  const [purpose, setPurpose] = useState<AdminConversationAccessPurpose>("user_support");
  const [reason, setReason] = useState("");
  const searchParamsKey = searchParams.toString();
  const query = useMemo(() => parseConversationQuery(searchParams), [searchParamsKey]);
  const modelDependencies = useMemo(
    () => ({ ...props.dependencies, locale, query }),
    [props.dependencies, locale, query],
  );
  const state = useConversationHistoryScreenModel(modelDependencies, manualRefreshSignal);
  const contentAccess = useConversationContentAccessModel(modelDependencies);
  const canAccessContent = hasAdminPermission(
    props.dependencies.adminPermissions,
    "admin.conversations.content.read",
  );
  const refreshCurrentScreen = useCallback(() => {
    setManualRefreshSignal((current) => current + 1);
  }, []);
  useAdminManualRefreshRegistration(useMemo(
    () => ({ onRefresh: refreshCurrentScreen, refreshing: state.status === "loading" }),
    [refreshCurrentScreen, state.status],
  ));

  useEffect(() => {
    setSelectedRow(undefined);
    contentAccess.clear();
  }, [searchParamsKey]);

  const copy = {
    section: pickAdminDashboardLocaleText(locale, { en: "Conversations", ko: "대화" }),
    title: pickAdminDashboardLocaleText(locale, { en: "Conversation History", ko: "대화 내역" }),
    description: pickAdminDashboardLocaleText(locale, {
      en: "Find signed-in and public-pet conversation sessions. Message content stays closed until an authorized security operator records a purpose and reason.",
      ko: "로그인 보호자와 공개 펫 대화 세션을 찾습니다. 메시지 원문은 권한이 있는 보안 담당자가 목적과 사유를 기록한 뒤에만 열립니다.",
    }),
    from: pickAdminDashboardLocaleText(locale, { en: "From", ko: "시작" }),
    to: pickAdminDashboardLocaleText(locale, { en: "To", ko: "종료" }),
    source: pickAdminDashboardLocaleText(locale, { en: "Source", ko: "대화 유형" }),
    allSources: pickAdminDashboardLocaleText(locale, { en: "All", ko: "전체" }),
    owner: pickAdminDashboardLocaleText(locale, { en: "Signed-in owner", ko: "로그인 보호자" }),
    publicPet: pickAdminDashboardLocaleText(locale, { en: "Public pet", ko: "공개 펫" }),
    internalUserId: pickAdminDashboardLocaleText(locale, { en: "Internal user ID", ko: "내부 사용자 ID" }),
    apply: pickAdminDashboardLocaleText(locale, { en: "Apply filters", ko: "필터 적용" }),
    reset: pickAdminDashboardLocaleText(locale, { en: "Reset", ko: "초기화" }),
    loading: pickAdminDashboardLocaleText(locale, { en: "Loading conversation sessions...", ko: "대화 세션을 불러오는 중입니다..." }),
    denied: pickAdminDashboardLocaleText(locale, { en: "Conversation access denied", ko: "대화 조회 권한 없음" }),
    failed: pickAdminDashboardLocaleText(locale, { en: "Conversation lookup failed", ko: "대화 조회 실패" }),
    empty: pickAdminDashboardLocaleText(locale, { en: "No conversation sessions", ko: "대화 세션 없음" }),
    emptyBody: pickAdminDashboardLocaleText(locale, { en: "No session matches the current filters.", ko: "현재 필터 조건에 맞는 대화 세션이 없습니다." }),
    actor: pickAdminDashboardLocaleText(locale, { en: "User", ko: "사용자" }),
    target: pickAdminDashboardLocaleText(locale, { en: "Conversation target", ko: "대화 대상" }),
    messages: pickAdminDashboardLocaleText(locale, { en: "Messages", ko: "메시지" }),
    availability: pickAdminDashboardLocaleText(locale, { en: "Availability", ko: "가용 상태" }),
    activeTurn: pickAdminDashboardLocaleText(locale, { en: "Active turn", ko: "활성 턴" }),
    updated: pickAdminDashboardLocaleText(locale, { en: "Updated", ko: "업데이트" }),
    select: pickAdminDashboardLocaleText(locale, { en: "Select", ko: "선택" }),
    selectedSession: pickAdminDashboardLocaleText(locale, { en: "Selected session", ko: "선택한 세션" }),
    selectGuide: pickAdminDashboardLocaleText(locale, { en: "Select a session from the table to inspect its metadata.", ko: "표에서 세션을 선택하면 메타데이터를 확인할 수 있습니다." }),
    protectedTitle: pickAdminDashboardLocaleText(locale, { en: "Protected message content", ko: "보호된 메시지 원문" }),
    protectedBody: pickAdminDashboardLocaleText(locale, { en: "Content is not loaded automatically. Every access request records the operator, target hash, purpose, reason, result, and time.", ko: "원문은 자동으로 불러오지 않습니다. 모든 접근 요청은 담당자, 대상 해시, 목적, 사유, 결과, 시각을 감사 기록으로 남깁니다." }),
    contentDenied: pickAdminDashboardLocaleText(locale, { en: "Your role can inspect session metadata but cannot open message content.", ko: "현재 역할은 세션 메타데이터만 확인할 수 있고 메시지 원문은 열 수 없습니다." }),
    purpose: pickAdminDashboardLocaleText(locale, { en: "Access purpose", ko: "접근 목적" }),
    reason: pickAdminDashboardLocaleText(locale, { en: "Detailed reason", ko: "상세 사유" }),
    reasonPlaceholder: pickAdminDashboardLocaleText(locale, { en: "Enter at least 12 characters describing the case.", ko: "대상 문의나 사고 조사 사유를 12자 이상 입력하세요." }),
    openContent: pickAdminDashboardLocaleText(locale, { en: "Record audit and open messages", ko: "감사 기록 후 메시지 열기" }),
    opening: pickAdminDashboardLocaleText(locale, { en: "Recording access and loading messages...", ko: "접근을 기록하고 메시지를 불러오는 중입니다..." }),
    contentFailed: pickAdminDashboardLocaleText(locale, { en: "Message access failed", ko: "메시지 원문 조회 실패" }),
    noMessages: pickAdminDashboardLocaleText(locale, { en: "No visible messages", ko: "표시할 메시지 없음" }),
    audit: pickAdminDashboardLocaleText(locale, { en: "Audit ID", ko: "감사 ID" }),
    transcript: pickAdminDashboardLocaleText(locale, { en: "Conversation messages", ko: "대화 메시지" }),
    total: pickAdminDashboardLocaleText(locale, { en: "Total sessions", ko: "전체 세션" }),
  };

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setSearchParams(createFilterSearchParams(new FormData(event.currentTarget), locale));
  }

  function handleSelect(row: ConversationRow): void {
    setSelectedRow(row);
    contentAccess.clear();
    setReason("");
  }

  function handleContentSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (!selectedRow || reason.trim().length < 12) {
      return;
    }
    void contentAccess.access({
      source: selectedRow.source,
      sessionId: selectedRow.sessionId,
      purpose,
      reason: reason.trim(),
    });
  }

  const readyState = state.status === "ready" || state.status === "partial" || state.status === "stale"
    ? state
    : undefined;

  return (
    <section style={{ display: "grid", gap: "18px" }}>
      <header style={cardStyle}>
        <p style={{ margin: 0, color: "#9a3412", fontWeight: 700 }}>{copy.section}</p>
        <h3 style={{ margin: "8px 0 0", fontSize: "1.5rem", letterSpacing: "-0.03em" }}>{copy.title}</h3>
        <p style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.6, maxWidth: "52rem" }}>{copy.description}</p>
      </header>

      <form onSubmit={handleFilterSubmit}>
        <AdminFilterBar
          items={[
            { id: "from", label: copy.from, control: <input defaultValue={searchParams.get("from") ?? ""} id="from" name="from" placeholder="2026-08-01T00:00:00.000Z" style={controlStyle} /> },
            { id: "to", label: copy.to, control: <input defaultValue={searchParams.get("to") ?? ""} id="to" name="to" placeholder="2026-08-10T23:59:59.999Z" style={controlStyle} /> },
            { id: "source", label: copy.source, control: (
              <select defaultValue={searchParams.get("source") ?? ""} id="source" name="source" style={controlStyle}>
                <option value="">{copy.allSources}</option>
                <option value="owner">{copy.owner}</option>
                <option value="public_pet">{copy.publicPet}</option>
              </select>
            ) },
            { id: "internalUserId", label: copy.internalUserId, control: <input defaultValue={searchParams.get("internalUserId") ?? ""} id="internalUserId" name="internalUserId" style={controlStyle} /> },
          ]}
          actions={(
            <div style={{ display: "flex", gap: "10px" }}>
              <button style={primaryButtonStyle()} type="submit">{copy.apply}</button>
              <button
                onClick={() => setSearchParams(withAdminDashboardLocaleSearchParams(new URLSearchParams(), locale))}
                style={{ ...primaryButtonStyle(), background: "#fff", color: "#18212b", border: "1px solid rgba(148, 163, 184, 0.45)" }}
                type="button"
              >
                {copy.reset}
              </button>
            </div>
          )}
        />
      </form>

      {state.status === "idle" || state.status === "loading" ? (
        <section aria-live="polite" style={cardStyle}><p style={{ margin: 0 }}>{copy.loading}</p></section>
      ) : null}
      {state.status === "permission_denied" ? (
        <AdminErrorState title={copy.denied} body={state.error.message} requestId={state.error.requestId} tone="permission_denied" />
      ) : null}
      {state.status === "error" ? (
        <AdminErrorState title={copy.failed} body={state.error.message} requestId={state.error.requestId} tone={state.error.code === "ADMIN_QUERY_SOURCE_UNAVAILABLE" ? "source_unavailable" : "error"} />
      ) : null}
      {state.status === "empty" ? <AdminEmptyState title={copy.empty} body={copy.emptyBody} /> : null}

      {readyState ? (
        <>
          <section style={{ ...cardStyle, display: "flex", gap: "20px", flexWrap: "wrap" }}>
            <p style={{ margin: 0 }}><strong>{copy.total}</strong> {readyState.viewModel.totalCount}</p>
            <p style={{ margin: 0, color: "#64748b" }}>{readyState.viewModel.generatedAtLabel}</p>
            <p style={{ margin: 0, color: "#64748b" }}>Request {readyState.viewModel.requestId}</p>
          </section>
          <AdminDataTable
            rows={readyState.viewModel.rows}
            getRowId={(row) => `${row.source}:${row.sessionId}`}
            onRowClick={handleSelect}
            initialSortState={{ columnId: "updated", direction: "desc" }}
            columns={[
              { id: "source", label: copy.source, renderCell: (row) => row.sourceLabel, sortValue: (row) => row.sourceLabel },
              { id: "actor", label: copy.actor, renderCell: (row) => row.actorLabel, sortValue: (row) => row.actorLabel },
              { id: "target", label: copy.target, renderCell: (row) => row.targetLabel, sortValue: (row) => row.targetLabel },
              { id: "messages", label: copy.messages, renderCell: (row) => row.messageCount, sortValue: (row) => row.messageCount, align: "right" },
              { id: "availability", label: copy.availability, renderCell: (row) => row.availabilityLabel, sortValue: (row) => row.availabilityLabel },
              { id: "activeTurn", label: copy.activeTurn, renderCell: (row) => row.activeTurnStatusLabel, sortValue: (row) => row.activeTurnStatusLabel },
              { id: "updated", label: copy.updated, renderCell: (row) => row.updatedAtLabel, sortValue: (row) => row.updatedAt },
              {
                id: "action",
                label: copy.select,
                renderCell: (row) => (
                  <button
                    onClick={(event) => {
                      event.stopPropagation();
                      handleSelect(row);
                    }}
                    style={{ border: 0, background: "transparent", color: "#0f766e", cursor: "pointer", fontWeight: 700, padding: 0 }}
                    type="button"
                  >
                    {copy.select}
                  </button>
                ),
              },
            ]}
          />
        </>
      ) : null}

      <section style={cardStyle}>
        <h4 style={{ margin: 0, fontSize: "1.1rem" }}>{copy.selectedSession}</h4>
        {!selectedRow ? <p style={{ margin: "10px 0 0", color: "#64748b" }}>{copy.selectGuide}</p> : (
          <div style={{ marginTop: "14px", display: "grid", gap: "8px" }}>
            <p style={{ margin: 0 }}><strong>{selectedRow.sourceLabel}</strong> · {selectedRow.sessionId}</p>
            <p style={{ margin: 0, color: "#475569" }}>{copy.actor}: {selectedRow.actorLabel} · {copy.target}: {selectedRow.targetLabel}</p>
            <p style={{ margin: 0, color: "#475569" }}>{copy.messages}: {selectedRow.messageCount} · {copy.updated}: {selectedRow.updatedAtLabel}</p>
          </div>
        )}
      </section>

      {selectedRow ? (
        <section style={cardStyle}>
          <h4 style={{ margin: 0, fontSize: "1.1rem" }}>{copy.protectedTitle}</h4>
          <p style={{ margin: "10px 0 0", color: "#475569", lineHeight: 1.6 }}>{copy.protectedBody}</p>
          {!canAccessContent ? (
            <AdminErrorState title={copy.denied} body={copy.contentDenied} tone="permission_denied" />
          ) : (
            <form onSubmit={handleContentSubmit} style={{ marginTop: "18px", display: "grid", gap: "14px" }}>
              <label style={{ display: "grid", gap: "7px", fontWeight: 700 }}>
                {copy.purpose}
                <select value={purpose} onChange={(event) => setPurpose(event.target.value as AdminConversationAccessPurpose)} style={controlStyle}>
                  <option value="user_support">user_support</option>
                  <option value="security_review">security_review</option>
                  <option value="incident_investigation">incident_investigation</option>
                </select>
              </label>
              <label style={{ display: "grid", gap: "7px", fontWeight: 700 }}>
                {copy.reason}
                <textarea minLength={12} maxLength={500} required value={reason} onChange={(event) => setReason(event.target.value)} placeholder={copy.reasonPlaceholder} rows={4} style={{ ...controlStyle, resize: "vertical" }} />
              </label>
              <div><button disabled={reason.trim().length < 12 || contentAccess.state.status === "loading"} style={primaryButtonStyle(reason.trim().length < 12 || contentAccess.state.status === "loading")} type="submit">{copy.openContent}</button></div>
            </form>
          )}

          {contentAccess.state.status === "loading" ? <p aria-live="polite">{copy.opening}</p> : null}
          {contentAccess.state.status === "permission_denied" || contentAccess.state.status === "error" ? (
            <div style={{ marginTop: "16px" }}><AdminErrorState title={copy.contentFailed} body={contentAccess.state.error.message} requestId={contentAccess.state.error.requestId} tone={contentAccess.state.status === "permission_denied" ? "permission_denied" : "error"} /></div>
          ) : null}
          {contentAccess.state.status === "empty" ? <div style={{ marginTop: "16px" }}><AdminEmptyState title={copy.noMessages} body={`${copy.audit}: ${contentAccess.state.viewModel?.auditId ?? "-"}`} /></div> : null}
          {contentAccess.state.status === "ready" ? (
            <div style={{ marginTop: "20px" }}>
              <ConversationTranscript
                auditId={contentAccess.state.viewModel.auditId}
                auditLabel={copy.audit}
                messages={contentAccess.state.viewModel.messages}
                transcriptLabel={copy.transcript}
              />
            </div>
          ) : null}
        </section>
      ) : null}
    </section>
  );
}
